import { colors, CookieCategory, policyLink } from 'constants/global';
import { useEffect, useRef } from 'react';
import './CookieConsent.scss';
import { localeToSupportedTranslationFile } from 'constants/settings';
import { usePrevious } from 'shared/hooks/global';
import { useConfig } from 'shared/providers';
import { defaultMessages, useIntl } from './locales/index';
import { initialSetup } from './setupIntegrations';
export default function CookieConsent() {
  const {
    locale,
    cookieconsent,
    isLoadingTranslations,
    region
  } = useConfig();
  const intl = useIntl();
  const setupCompletedRef = useRef(false);
  useEffect(() => {
    /**
     * Overwrite some CSS variables from cookie consent to style it as Commutifi
     */
    document.documentElement.style.setProperty('--cc-text', colors.neutral600);
    document.documentElement.style.setProperty('--cc-btn-primary-bg', colors.primary400);
    document.documentElement.style.setProperty('--cc-btn-primary-hover-bg', colors.primary500);
    document.documentElement.style.setProperty('--cc-btn-secondary-bg', 'transparent');
    document.documentElement.style.setProperty('--cc-btn-secondary-hover-bg', 'transparent');
    document.documentElement.style.setProperty('--cc-cookie-category-block-bg', colors.neutral200);
    document.documentElement.style.setProperty('--cc-cookie-category-block-bg-hover', colors.neutral300);
    document.documentElement.style.setProperty('--cc-toggle-bg-off', colors.accentRed500);
  }, []);
  const previousLang = usePrevious(localeToSupportedTranslationFile(locale));
  useEffect(() => {
    const lang = localeToSupportedTranslationFile(locale);

    // GDPR Compliance: Cookie consent is REQUIRED for EU users regardless of login status.
    // Per Article 7(4) GDPR and ePrivacy Directive:
    // - Login status does NOT imply consent to analytics cookies (bundling prohibition)
    // - Prior consent is required before setting non-essential cookies
    // - Only "strictly necessary" cookies (session, security) are exempt
    // See: https://gdpr.eu/cookies/ and https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/lawful-basis/consent/what-is-valid-consent/

    // Use region from context (computed synchronously from timezone) instead of
    // cookieconsent object presence, which is set asynchronously and causes race conditions
    const isEURegion = region === 'Europe';

    // Only run setup once per session
    // Note: Sentry is already initialized early in index.tsx with masked mode.
    // This setup handles Google Analytics consent only.
    if (setupCompletedRef.current) {
      return;
    }

    // Non-EU regions: No GDPR/ePrivacy consent required
    // Enable full Sentry replay for better debugging (no PII concerns without EU regulations)
    if (!isEURegion) {
      initialSetup({
        enableFullReplay: true
      });
      setupCompletedRef.current = true;
      return;
    }

    // EU Region: Wait for cookieconsent library to be initialized
    // The cookieconsent object is set asynchronously in LocalConfigProvider's useEffect
    if (!cookieconsent) {
      return;
    }

    // EU Region: Check if user has already given valid consent
    const hasValidConsent = cookieconsent.validConsent();
    if (hasValidConsent) {
      // Valid consent exists - check what categories were accepted
      const userPreferences = cookieconsent.getUserPreferences();
      const acceptedCategories = userPreferences?.accepted_categories || [];
      const hasAcceptedAnalytics = acceptedCategories.includes(CookieCategory.Analytics) || acceptedCategories.includes(CookieCategory.All);

      // Setup Google Analytics based on prior consent
      initialSetup({
        enableFullReplay: hasAcceptedAnalytics
      });
      setupCompletedRef.current = true;
      // Don't return - we still need to initialize the consent library for preference changes
    }

    // EU Region without valid consent: Must show consent banner (even for logged-in users)
    // This is GDPR-compliant: we don't assume consent based on login status

    // Because we use lazy loading we dynamically add languages to the configuration by mutating the value.
    if (lang && lang !== cookieconsent.getConfig('current_lang')) {
      if (isLoadingTranslations) {
        // Short circuit, otherwise since we lazy load messages we will defined english content under the new lang (ex. french)
        // language
        return;
      }
      let langConfig;
      try {
        langConfig = cookieconsent?.getConfig('languages');
      } catch {
        // Config is not loaded yet - this happens before run() is called.
        // Don't return here - we need to proceed to run() below.
        langConfig = null;
      }
      if (langConfig && !langConfig[lang]) {
        // Config is loaded and language doesn't exist yet - add it
        // @ts-expect-error -- It's fine, we know it's defined. Since we are mutating we don't want to assign it to a const
        // to make typescript happy
        cookieconsent.getConfig('languages')[lang] = {
          consent_modal: {
            title: intl.formatMessage({
              id: 'cookies.consentPopup.title'
            }),
            description: `${intl.formatMessage({
              id: 'cookies.consentPopup.description'
            })} ` + `<button type="button" data-cc="c-settings" class="cc-link">${intl.formatMessage({
              id: 'cookies.consentPopup.manage'
            })}</button>`,
            primary_btn: {
              text: intl.formatMessage({
                id: 'cookies.consentPopup.buttons.acceptAll'
              }),
              role: 'accept_all' // 'accept_selected' or 'accept_all'
            },
            secondary_btn: {
              text: intl.formatMessage({
                id: 'cookies.consentPopup.buttons.onlyNecessary'
              }),
              role: 'accept_necessary' // 'settings' or 'accept_necessary'
            }
          },
          settings_modal: {
            title: intl.formatMessage({
              id: 'cookies.consentPopup.title'
            }),
            save_settings_btn: intl.formatMessage({
              id: 'cookies.consentPopup.buttons.save'
            }),
            accept_all_btn: intl.formatMessage({
              id: 'cookies.consentPopup.buttons.acceptAll'
            }),
            reject_all_btn: intl.formatMessage({
              id: 'cookies.consentPopup.buttons.onlyNecessary'
            }),
            close_btn_label: intl.formatMessage({
              id: 'cookies.consentPopup.buttons.close'
            }),
            blocks: [{
              title: intl.formatMessage({
                id: 'cookies.consentPopup.settings.usage.title'
              }),
              description: `${intl.formatMessage({
                id: 'cookies.consentPopup.description'
              })} ` + `<a class="cc-link" href="${policyLink}" target="__blank">${intl.formatMessage({
                id: 'privacyPolicy'
              })}</a>`
            }, {
              title: intl.formatMessage({
                id: 'cookies.consentPopup.settings.necessary.title'
              }),
              description: intl.formatMessage({
                id: 'cookies.consentPopup.settings.necessary.description'
              }),
              toggle: {
                value: CookieCategory.Necessary,
                enabled: true,
                readonly: true // cookie categories with readonly=true are all treated as "necessary cookies"
              }
            }, {
              title: intl.formatMessage({
                id: 'cookies.consentPopup.settings.performance.title'
              }),
              description: intl.formatMessage({
                id: 'cookies.consentPopup.settings.performance.description'
              }),
              toggle: {
                value: CookieCategory.Analytics,
                // your cookie category
                enabled: false,
                readonly: false
              }
            }, {
              title: intl.formatMessage({
                id: 'cookies.consentPopup.settings.info.title'
              }),
              description: intl.formatMessage({
                id: 'cookies.consentPopup.settings.info.description'
              })
            }]
          }
        };
        cookieconsent.updateLanguage(lang, true);
      }
    }

    // At this point we're in EU region with cookieconsent initialized - run the consent banner
    cookieconsent.run({
      gui_options: {
        consent_modal: {
          layout: 'bar',
          // box/cloud/bar
          position: 'bottom center',
          // bottom/middle/top + left/right/center
          transition: 'slide',
          // zoom/slide
          swap_buttons: true // enable to invert buttons
        },
        settings_modal: {
          layout: 'box',
          // box/bar
          // position: 'left',           // left/right
          transition: 'slide' // zoom/slide
        }
      },
      languages: {
        en: {
          consent_modal: {
            title: defaultMessages['cookies.consentPopup.title'],
            description: `${defaultMessages['cookies.consentPopup.description']} ` + `<button type="button" data-cc="c-settings" class="cc-link">${defaultMessages['cookies.consentPopup.manage']}</button>`,
            primary_btn: {
              text: defaultMessages['cookies.consentPopup.buttons.acceptAll'],
              role: 'accept_all' // 'accept_selected' or 'accept_all'
            },
            secondary_btn: {
              text: defaultMessages['cookies.consentPopup.buttons.onlyNecessary'],
              role: 'accept_necessary' // 'settings' or 'accept_necessary'
            }
          },
          settings_modal: {
            title: defaultMessages['cookies.consentPopup.title'],
            save_settings_btn: defaultMessages['cookies.consentPopup.buttons.save'],
            accept_all_btn: defaultMessages['cookies.consentPopup.buttons.acceptAll'],
            reject_all_btn: defaultMessages['cookies.consentPopup.buttons.onlyNecessary'],
            close_btn_label: defaultMessages['cookies.consentPopup.buttons.close'],
            blocks: [{
              title: defaultMessages['cookies.consentPopup.settings.usage.title'],
              description: `${defaultMessages['cookies.consentPopup.description']} ` + `<a class="cc-link" href="${policyLink}" target="__blank">${defaultMessages.privacyPolicy}</a>`
            }, {
              title: defaultMessages['cookies.consentPopup.settings.necessary.title'],
              description: defaultMessages['cookies.consentPopup.settings.necessary.description'],
              toggle: {
                value: CookieCategory.Necessary,
                enabled: true,
                readonly: true // cookie categories with readonly=true are all treated as "necessary cookies"
              }
            }, {
              title: defaultMessages['cookies.consentPopup.settings.performance.title'],
              description: defaultMessages['cookies.consentPopup.settings.performance.description'],
              toggle: {
                value: CookieCategory.Analytics,
                // your cookie category
                enabled: false,
                readonly: false
              }
            }, {
              title: defaultMessages['cookies.consentPopup.settings.info.title'],
              description: defaultMessages['cookies.consentPopup.settings.info.description']
            }]
          }
        }
      },
      current_lang: localeToSupportedTranslationFile(locale),
      autoclear_cookies: true,
      // default: false
      page_scripts: true,
      // default: false

      // mode: 'opt-in'                          // default: 'opt-in'; value: 'opt-in' or 'opt-out'
      // delay: 0,                               // default: 0
      // auto_language: null                     // default: null; could also be 'browser' or 'document'
      // GDPR: Always show consent banner for EU users (even logged-in users)
      // Login status does NOT imply cookie consent per Article 7(4) GDPR
      autorun: true,
      // force_consent: false,                   // default: false
      // hide_from_bots: false,                  // default: false
      remove_cookie_tables: true,
      // default: false
      // cookie_name: 'cc_cookie',               // default: 'cc_cookie'
      // cookie_expiration: 182,                 // default: 182 (days)
      // cookie_necessary_only_expiration: 182   // default: disabled
      // cookie_domain: location.hostname,       // default: current domain
      // cookie_path: '/',                       // default: root
      // cookie_same_site: 'Lax',                // default: 'Lax'
      // use_rfc_cookie: false,                  // default: false
      // revision: 0,                            // default: 0

      onFirstAction() {
        // callback triggered only once
      },
      onAccept(cookie) {
        // Handle Google Analytics consent based on cookie decision
        // Note: Sentry is already initialized early (index.tsx) with masked mode.
        // Full Sentry replay requires page reload after consent change.
        const acceptedAnalytics = cookie.categories.findIndex(cat_0 => cat_0 === CookieCategory.Analytics) >= 0 || cookie.categories.findIndex(cat => cat === CookieCategory.All) >= 0;
        if (!setupCompletedRef.current) {
          initialSetup({
            enableFullReplay: acceptedAnalytics
          });
          setupCompletedRef.current = true;
        }
      },
      onChange(_cookie, changedCategories) {
        // Handle consent changes after initial setup
        // This fires when user updates their preferences in the settings modal
        if (setupCompletedRef.current) {
          // Note: Sentry Replay privacy settings are "baked in" at init time and cannot be
          // changed dynamically. If analytics consent changes, the new privacy settings will
          // only take effect on the user's next page load/session.
          // See: https://docs.sentry.io/platforms/javascript/session-replay/configuration/
          const analyticsChanged = changedCategories.includes(CookieCategory.Analytics);
          if (analyticsChanged) {
            // We intentionally do NOT reload the page here. The user's new consent choice
            // is saved to the cc_cookie, and Sentry will initialize with the correct
            // privacy settings on their next natural page load.
          }
        }
      }
    });
  }, [cookieconsent, intl, isLoadingTranslations, locale, previousLang, region]);
  return null;
}